import React, {ReactElement} from 'react'
import {
    Box,
    Step,
    StepConnector,
    StepContent,
    StepContentProps,
    StepIconProps,
    StepLabel,
    Stepper,
} from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import {Colors, FontWeights} from '../../../../styledComponents/Constants'
import styled from 'styled-components'
import {
    LeftColumnFlexContainer,
    LeftRowFlexContainer,
    TopRowFlexContainer
} from '../../../../styledComponents/FlexComponents'
import {StyledDiv} from '../../../../styledComponents/StyledTypeDefinitions'
import {Check, Close, PlaylistAddCheck} from '@mui/icons-material'
import {OWNERSHIP_TRANSFER_STATUS} from '../../../../CodeMaster'

interface Props {
    process: OwnershipTransferProcess[]
}

export interface OwnershipTransferProcess {
    processName: string
    rep: RepInfo[]
    processLatestDateTime?: string
    completed: boolean
    memo?: string
}

interface RepInfo {
    rep: string
    inputCompleteDateTime?: string
}

export const NatOwnershipTransferStatus: React.FC<Props> = props => {
    const {process} = props

    const StepIconComponent = (stepIconProps: StepIconProps): ReactElement => {
        const {active, completed} = stepIconProps

        return (
            <StepIconContainer $active={active}>
                {completed ? <CheckIcon/> : <CircleIcon/>}
            </StepIconContainer>
        )
    }

    const StepRejectionIconComponent = (stepIconProps: StepIconProps): ReactElement => {
        const {active} = stepIconProps

        return (
            <StepIconContainer $active={active}>
                <CloseIcon/>
            </StepIconContainer>
        )
    }

    const activeStep = Math.max(
        ...process.map((step, index) => step.completed ? index + 1 : -1)
    )

    return (
        <Box>
            <StyledStepper orientation="vertical" activeStep={activeStep} connector={<StepperStyles/>}>
                {process.map((step, index) => (
                    <Step expanded={true} key={index}>
                        <StepLabel style={{height: '25px'}}
                                   StepIconComponent={(step.processName === OWNERSHIP_TRANSFER_STATUS.INVALID.label)
                            ? StepRejectionIconComponent
                            : StepIconComponent
                        }>
                            <TopRowFlexContainer>
                                <OutlineContainer>
                                    <ProcessTitle>{step.processName}</ProcessTitle>
                                </OutlineContainer>
                                <DetailContainer>
                                    {step.rep.map((re, repIndex) => {
                                        return (
                                            <Box minWidth={110} mr={'38px'} key={repIndex}>
                                                <ItemTitleArea>
                                                    担当者
                                                </ItemTitleArea>
                                            </Box>
                                        )
                                    })}
                                </DetailContainer>
                            </TopRowFlexContainer>
                        </StepLabel>
                        <StyledStepContent incomplete={!step.completed}>
                            <TopRowFlexContainer>
                                <OutlineContainer>
                                    <ProcessCompleteDate>{step.processLatestDateTime}</ProcessCompleteDate>
                                </OutlineContainer>
                                {step.rep.length > 0 &&
                                    <DetailContainer>
                                        {step.rep.map((re, repIndex) => {
                                            return (
                                                <Box minWidth={110} mr={'38px'} key={repIndex}>
                                                    <LeftColumnFlexContainer>
                                                        <Box>{re.rep}</Box>
                                                        {re.inputCompleteDateTime &&
                                                            <Box>{re.inputCompleteDateTime}</Box>
                                                        }
                                                    </LeftColumnFlexContainer>
                                                </Box>
                                            )
                                        })}
                                    </DetailContainer>
                                }
                                {step.memo &&
                                    <DetailContainer>
                                        <NoteContainer><PlaylistAddCheckIcon/>{step.memo}</NoteContainer>
                                    </DetailContainer>
                                }
                            </TopRowFlexContainer>
                        </StyledStepContent>
                    </Step>
                ))}
            </StyledStepper>
        </Box>
    )
}

const StepperStyles = withStyles({
    active: {
        '& $line': {
            '&:before': {
                background: Colors.AdminPrimary,
            },
        },
    },
    completed: {
        '& $line': {
            '&:before': {
                background: Colors.AdminPrimary,
            },
        },
    },
    vertical: {
        height: 40,
        padding: 0,
        position: 'relative',
    },
    line: {
        border: 'none',
        '&:before': {
            position: 'absolute',
            content: '""',
            width: 3,
            background: Colors.AccentLight,
            height: '100%',
            borderRadius: '0 0 1px 1px',
        },
    },
})(StepConnector)

const StyledStepper = withStyles({
    root: {
        padding: '24px 32px 40px 32px',
    },
})(Stepper)

const StepIconContainer = styled.div<{ $active?: boolean }>`
    color: ${props => props.$active ? Colors.AdminPrimary : Colors.AccentLight};
  width: 28px;
  height: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const CheckIcon = withStyles({
    root: {
        color: Colors.AdminPrimary,
        zIndex: 1,
        fontSize: 18,
    },
})(Check)

const CloseIcon = withStyles({
    root: {
        color: 'grey',
        stroke: 'white',
        strokeWidth: 0.5,
        zIndex: 1,
        fontSize: 24,
    },
})(Close)

const CircleIcon = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: currentColor;
`

const OutlineContainer = styled(LeftColumnFlexContainer)`
  width: 142px;
`

const DetailContainer = styled(LeftRowFlexContainer)`
  flex: 1;
  align-items: flex-start;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`

const ProcessTitle: StyledDiv = styled.div`
  font-size: 16px;
  font-weight: ${FontWeights.Bold};
`

const ProcessCompleteDate: StyledDiv = styled.div`
  font-size: 14px;
  color: ${Colors.AccentDark};
`

const ItemTitleArea = styled.div<{ highlight?: boolean }>`
  font-size: 12px;
  background: ${props => props.highlight
    ? `linear-gradient(0deg, rgba(245, 166, 35, 0.2), rgba(245, 166, 35, 0.2)), ${Colors.Surface}`
    : Colors.Background};
  padding: 1px 16px;
  color: ${Colors.Body};
  margin-bottom: 4px;
  height: 20px;
  line-height: 18px;
  display: inline-block;
`

const NoteContainer = styled.div`
  font-size: 12px;
  align-items: center;
  display: flex;
  padding-top: 4px;
  line-height: 18px;
  margin-top: -15px;
`

const StyledStepContent = styled(({incomplete, ...props}) =>
    (<StepContent {...props} />))<StepContentProps & { incomplete?: boolean }>`
  &.MuiStepContent-root {
    position: relative;
    margin-top: 0;
    padding-left: 23px;
    border-left: none;

    &:before {
      position: absolute;
      content: '';
      width: 3px;
      background: ${props => props.incomplete ? Colors.AccentLight : Colors.AdminPrimary};
      height: 100%;
      border-radius: 1px 1px 0 0;
      left: 0;
    }
  }

  &.MuiStepContent-last {
    border-left: none;

    &:before {
      position: absolute;
      content: none;
    }
  }
`

const PlaylistAddCheckIcon = withStyles({
    root: {
        color: Colors.AccentDark,
        fontSize: 18,
        marginRight: 2.75,
    },
})(PlaylistAddCheck)
